import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { quizService, QuizAnalytics as QuizAnalyticsData } from '../../services/quizService';
import { AcademicCapIcon } from '@heroicons/react/24/outline';

const QuizAnalytics: React.FC = () => {
  const [data, setData] = useState<QuizAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuizAnalytics();
  }, []);

  const fetchQuizAnalytics = async () => {
    try {
      setLoading(true);
      const analyticsData = await quizService.getAnalytics();
      setData(analyticsData);
    } catch (error) {
      console.error('Failed to fetch quiz analytics:', error);
      toast.error('Failed to load quiz analytics');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get profile color
  const getProfileColor = (profileName: string): { bg: string; text: string; border: string } => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      'The Safety-First Learner': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
      'The Structured Learner': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
      'The Sensory-Sensitive Learner': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
      'The Individualized-Pace Learner': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
      'The Interest-Driven Learner': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' }
    };
    return colors[profileName] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
  };

  // Helper to get profile bar color (darker for bars)
  const getProfileBarColor = (profileName: string): string => {
    const colors: Record<string, string> = {
      'The Safety-First Learner': 'bg-red-500',
      'The Structured Learner': 'bg-blue-500',
      'The Sensory-Sensitive Learner': 'bg-purple-500',
      'The Individualized-Pace Learner': 'bg-green-500',
      'The Interest-Driven Learner': 'bg-yellow-500'
    };
    return colors[profileName] || 'bg-gray-500';
  };

  // Format date as relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Empty state
  if (!data || data.total_completed === 0) {
    return (
      <div className="text-center py-12">
        <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No quiz data yet</h3>
        <p className="mt-1 text-sm text-gray-500">Quiz completions will appear here once parents start taking the quiz.</p>
      </div>
    );
  }

  // Get chatbot and quiz lead counts
  const chatbotLeads = data.completion_by_source.find(s => s.source === 'chatbot')?.total || 0;
  const quizLeads = data.completion_by_source.find(s => s.source === 'quiz')?.completed || data.total_completed;

  // Calculate max count for profile distribution bars
  const maxCount = Math.max(...data.profile_distribution.map(p => p.count));

  // Sort profile distribution by count (highest first)
  const sortedProfiles = [...data.profile_distribution].sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Quiz Analytics</h2>
        <p className="mt-1 text-sm text-gray-600">
          Track quiz completion rates and learning profile distribution
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Quizzes Completed */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Quizzes</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{data.total_completed}</p>
            </div>
            <div className="bg-orange-100 rounded-full p-3">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>

        {/* Average Quiz Score */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Score</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{data.average_score}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </div>

        {/* Chatbot Leads */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Chatbot Leads</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{chatbotLeads}</p>
            </div>
            <div className="bg-blue-50 rounded-full p-3">
              <span className="text-2xl">💬</span>
            </div>
          </div>
        </div>

        {/* Quiz Leads */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Quiz Leads</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{quizLeads}</p>
            </div>
            <div className="bg-orange-50 rounded-full p-3">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Distribution */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Profile Distribution</h3>
        <p className="text-sm text-gray-600 mb-6">
          Breakdown of quiz results across the 5 learning profiles
        </p>

        <div className="space-y-4">
          {sortedProfiles.map((profile, index) => {
            const percentage = data.total_completed > 0
              ? Math.round((profile.count / data.total_completed) * 100)
              : 0;
            const barWidth = maxCount > 0 ? (profile.count / maxCount) * 100 : 0;
            const colors = getProfileColor(profile.profile_name);
            const barColor = getProfileBarColor(profile.profile_name);

            return (
              <div key={index}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}>
                    {profile.profile_name.replace('The ', '').replace(' Learner', '')}
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {profile.count} ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className={`${barColor} h-6 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2`}
                    style={{ width: `${barWidth}%` }}
                  >
                    {barWidth > 15 && (
                      <span className="text-xs font-semibold text-white">{percentage}%</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dimension Analytics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Average Dimension Scores</h3>
        <p className="text-sm text-gray-600 mb-6">
          Which challenges do parents face most? (Across all quiz completions)
        </p>

        {data.dimension_analytics.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No dimension data available</p>
        ) : (
          <div className="space-y-4">
            {data.dimension_analytics.map((dimension) => {
              const dimensionNames: Record<string, string> = {
                sensory: 'Sensory Support',
                structure: 'Structure & Routine',
                pacing: 'Individualized Pacing',
                confidence: 'Emotional Safety',
                interest: 'Interest-Driven Learning'
              };

              const dimensionColors: Record<string, string> = {
                sensory: 'bg-purple-500',
                structure: 'bg-blue-500',
                pacing: 'bg-green-500',
                confidence: 'bg-red-500',
                interest: 'bg-yellow-500'
              };

              return (
                <div key={dimension.dimension} className="flex items-center">
                  <div className="w-48 flex-shrink-0">
                    <span className="text-sm font-medium text-gray-700">
                      {dimensionNames[dimension.dimension] || dimension.dimension}
                    </span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${dimensionColors[dimension.dimension] || 'bg-gray-500'} transition-all duration-500 flex items-center justify-end pr-3`}
                        style={{ width: `${dimension.avg_percentage}%` }}
                      >
                        {dimension.avg_percentage > 15 && (
                          <span className="text-xs font-medium text-white">
                            {dimension.avg_percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-20 flex-shrink-0 text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      {dimension.avg_percentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Completions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Quiz Completions</h3>
        <p className="text-sm text-gray-600 mb-6">
          Last 10 quiz submissions
        </p>

        {data.recent_completions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No recent completions</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Child Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.recent_completions.map((completion) => (
                  <tr key={completion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {completion.parent_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {completion.child_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {completion.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-300">
                        {completion.quiz_score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(completion.quiz_completed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizAnalytics;
